import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  approveAccount,
  demoteAccount,
  fetchApprovalQueue,
  promoteAccount,
  rejectAccount,
  type AccountRow,
  type ApprovalQueue,
} from "../../services/http.js";

/**
 * Who is waiting to be let in, and who is already here — phase 19a, ADR 0021,
 * extended by **ADR 0028**.
 *
 * # It grants privilege now, and that is a reversal
 *
 * This file used to say: *"There is no button here that makes an admin and
 * there is not going to be one … promoting is still a deliberate act with
 * `sonolume-ingest role`."* The amendment to ADR 0027 made an admin the **only**
 * person who can let anybody into the installation, so the capability stopped
 * being one two people need. The reasoning survives as the shape of the screen:
 * admin-only, 404 to everybody else, and every decision logged server-side.
 *
 * # 🔴 The last administrator cannot be removed
 *
 * `/setup` is offered exactly while no admin exists, so an installation that
 * loses its last one puts its own first-run route back on a live deployment.
 * The control is disabled when `last_admin` says so — ⚠️ **as a courtesy, not as
 * the check.** The server refuses it regardless, and has to, because
 * `sonolume-ingest` never renders this.
 *
 * # Two lists, because one would hide people
 *
 * Waiting is a queue: FIFO, because somebody is on the other end of it. Here is
 * a directory: newest first, because the person you are looking for is the one
 * who just arrived. Both are capped, separately — sorted into one list, a wave
 * of registrations would fill the page and make every existing account
 * invisible.
 *
 * # Rejecting keeps the row
 *
 * Deliberately not a delete. A deleted row frees the address to register again
 * and rejoin this list, which turns "no" into "not yet" — see migration 0023.
 * And since this screen lists settled accounts, a rejection is now **reversible
 * from here**: approve works on a rejected row. Before 20f-3 it was `psql`.
 *
 * # A failed load reads as "nothing here"
 *
 * The server answers 404 to anybody who is not an admin, same as every sibling
 * panel, and this must not render that as "forbidden".
 */
export function AccountsPanel() {
  const { t } = useTranslation();
  const [queue, setQueue] = useState<ApprovalQueue | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  const load = useCallback(() => {
    void fetchApprovalQueue().then(
      (answer) => {
        setQueue(answer);
        setMissing(false);
      },
      () => {
        setMissing(true);
      },
    );
  }, []);

  useEffect(load, [load]);

  /**
   * Every action is the same shape: call, then re-read.
   *
   * ⚠️ **Re-read rather than patch the row locally.** 19a could drop the row and
   * be right, because the only outcome was leaving the queue. A decision now
   * moves an account *between* the two lists, can change two fields at once
   * (promoting approves on the way), and can be refused — so a local edit would
   * be this screen guessing at an answer the server already sent.
   */
  const act = useCallback(
    (id: string, call: (id: string) => Promise<unknown>) => {
      setBusy(id);
      setRefused(null);
      void call(id).then(
        () => {
          setBusy(null);
          load();
        },
        (cause: unknown) => {
          setBusy(null);
          // A 409 is a decision, not a failure: it says what to do instead —
          // promote somebody else first, demote them first — and the server's
          // sentence is more specific than anything this screen could guess.
          setRefused(cause instanceof Error ? cause.message : t("admin.accounts.failed"));
          load();
        },
      );
    },
    [load, t],
  );

  if (missing) {
    return <p className="text-sm text-white/50">{t("admin.nothingHere")}</p>;
  }
  if (!queue) {
    return <p className="text-sm text-white/50">{t("admin.accounts.reading")}</p>;
  }

  return (
    <section aria-label={t("admin.accounts.label")} className="flex flex-col gap-6">
      {refused && (
        <p role="alert" className="text-sm text-amber-300">
          {refused}
        </p>
      )}

      <div className="flex flex-col gap-3">
        <p className="text-xs text-white/40">
          {t("admin.accounts.summary", { waiting: queue.pending })}
        </p>
        {queue.waiting.length === 0 ? (
          <p className="text-sm text-white/60">{t("admin.accounts.empty")}</p>
        ) : (
          queue.waiting.map((row) => (
            <Row key={row.id} row={row} busy={busy === row.id} act={act} />
          ))
        )}
        {queue.pending > queue.waiting.length && (
          <p className="text-xs text-white/40">
            {t("admin.accounts.more", {
              shown: queue.waiting.length,
              total: queue.pending,
            })}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-medium text-white/70">
          {t("admin.accounts.hereTitle")}
        </h3>
        {queue.settled.map((row) => (
          <Row key={row.id} row={row} busy={busy === row.id} act={act} />
        ))}
        {queue.total > queue.settled.length && (
          <p className="text-xs text-white/40">
            {t("admin.accounts.more", {
              shown: queue.settled.length,
              total: queue.total,
            })}
          </p>
        )}
      </div>
    </section>
  );
}

/** One account, with whatever decisions are open on it. */
function Row({
  row,
  busy,
  act,
}: {
  row: AccountRow;
  busy: boolean;
  act: (id: string, call: (id: string) => Promise<unknown>) => void;
}) {
  // `useTranslation` here rather than a `t` prop: the key union is what catches
  // a missing translation at compile time, and it only survives while `t` is
  // the real one — a loosely typed prop would take any string.
  const { t } = useTranslation();
  const admin = row.role === "admin";

  return (
    <article className="flex flex-wrap items-center gap-3 rounded-lg bg-white/5 p-3">
      <div className="min-w-0 grow">
        <p className="truncate text-sm text-white">
          {row.display_name ?? t("admin.accounts.noName")}
          {admin && (
            <span className="ml-2 rounded bg-flash-500/20 px-1.5 py-0.5 text-xs text-flash-300">
              {t("admin.accounts.isAdmin")}
            </span>
          )}
        </p>
        <p className="truncate text-xs text-white/50">{row.email}</p>
        {!row.email_verified && (
          // The one fact here that is not self-asserted. Worth seeing before
          // letting somebody in, and worth not hiding in a tooltip.
          <p className="text-xs text-amber-300/80">{t("admin.accounts.unverified")}</p>
        )}
        {row.status === "rejected" && (
          <p className="text-xs text-white/40">{t("admin.accounts.wasRejected")}</p>
        )}
      </div>

      <div className="flex shrink-0 flex-wrap gap-2">
        {row.status !== "active" && (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              act(row.id, approveAccount);
            }}
            className="rounded-md bg-emerald-500/20 px-3 py-1.5 text-sm text-emerald-200 disabled:opacity-50"
          >
            {t(
              row.status === "rejected"
                ? "admin.accounts.restore"
                : "admin.accounts.approve",
            )}
          </button>
        )}
        {row.status !== "rejected" && !admin && (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              act(row.id, rejectAccount);
            }}
            className="rounded-md bg-white/10 px-3 py-1.5 text-sm text-white/70 disabled:opacity-50"
          >
            {t("admin.accounts.reject")}
          </button>
        )}
        {admin ? (
          <button
            type="button"
            // 🔴 The last admin: disabled here, refused by the server. Offering
            // a control that is going to be refused is worse than not offering
            // it, and hiding it entirely would leave no way to learn why.
            disabled={busy || row.last_admin}
            title={row.last_admin ? t("admin.accounts.lastAdmin") : undefined}
            onClick={() => {
              act(row.id, demoteAccount);
            }}
            className="rounded-md bg-white/10 px-3 py-1.5 text-sm text-white/70 disabled:opacity-50"
          >
            {t("admin.accounts.demote")}
          </button>
        ) : (
          row.status !== "rejected" && (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                act(row.id, promoteAccount);
              }}
              className="rounded-md bg-flash-500/20 px-3 py-1.5 text-sm text-flash-200 disabled:opacity-50"
            >
              {t("admin.accounts.promote")}
            </button>
          )
        )}
      </div>
    </article>
  );
}
